import { type ApiClient } from '../../../shared/api/api-client.js';

const CURRENCY_FRACTION_DIGITS = 1 + 1;
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 50;

export interface CatalogueLocationNode {
  readonly id: string;
  readonly title: string;
  readonly type: 'location' | 'hotel';
  readonly nodes?: readonly CatalogueLocationNode[] | undefined;
}

export interface CatalogueLocationResult {
  readonly id: string;
  readonly name: string;
  readonly displayName?: string | undefined;
  readonly description?: string | undefined;
  readonly imageUrl?: string | undefined;
  readonly address?: string | undefined;
  readonly starRating?: number | undefined;
  readonly currency: string;
  readonly countryCode: string;
  readonly stateCode?: string | undefined;
  readonly cityCode: string;
  readonly city: string;
  readonly logo?: string | undefined;
  readonly latitude?: string | undefined;
  readonly longitude?: string | undefined;
  readonly lowestPrice: {
    readonly baseAmount: string;
    readonly amount: string;
    readonly currency: string;
    readonly nights: number;
    readonly ratePlanId: string;
    readonly ratePlanName: string;
  };
  readonly roomTypes?: readonly unknown[] | undefined;
}

export interface CatalogueRateBreakdownNight {
  readonly date: string;
  readonly rate: string;
  readonly baseRate: string;
  readonly ratePlanId: string;
}

export interface CatalogueRate {
  readonly ratePlanId: string;
  readonly ratePlanName: string;
  readonly ratePlanType: string;
  readonly mixedRate: boolean;
  readonly rooms: number;
  readonly nights: number;
  readonly price: string;
  readonly discountedPrice: string;
  readonly discountedRoomTotal: string;
  readonly taxes: string;
  readonly fees: string;
  readonly discounts: string;
  readonly grandTotal: string;
  readonly currency: string;
  readonly rateBreakdown: readonly CatalogueRateBreakdownNight[];
  readonly cancellationPolicyId?: string | undefined;
  readonly cancellationPolicyName?: string | undefined;
  readonly paymentPolicyId?: string | undefined;
  readonly paymentPolicyName?: string | undefined;
  readonly inclusions: readonly string[];
  readonly appliedPromotionIds?: readonly string[] | undefined;
}

export interface CatalogueRoomCategory {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly description?: string | undefined;
  readonly imageUrl?: string | undefined;
  readonly images: readonly string[];
  readonly sizeInSqm?: string | undefined;
  readonly bedType?: string | undefined;
  readonly amenities: readonly string[];
  readonly maxOccupancy: number;
  readonly sellableRooms: number;
  readonly rates: readonly CatalogueRate[];
}

export interface CatalogueDetailedResult {
  readonly id: string;
  readonly name: string;
  readonly displayName?: string | undefined;
  readonly description?: string | undefined;
  readonly imageUrl?: string | undefined;
  readonly address?: string | undefined;
  readonly starRating?: number | undefined;
  readonly currency: string;
  readonly roomTypes: readonly CatalogueRoomCategory[];
}

export interface SearchResult {
  readonly rows: readonly CatalogueLocationResult[];
  readonly totalItems: number;
}

export interface SearchStaysParams {
  readonly checkIn: string;
  readonly checkOut: string;
  readonly adults: number;
  readonly children: number;
  readonly rooms: number;
  readonly promo?: string | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
  readonly propertyId?: string | undefined;
  readonly countryCode?: string | undefined;
  readonly stateCode?: string | undefined;
  readonly cityCode?: string | undefined;
}

export interface GetDetailedResultParams {
  readonly propertyId: string;
  readonly arrival: string;
  readonly departure: string;
  readonly adults: number;
  readonly children: number;
  readonly rooms: number;
  readonly promo?: string | undefined;
}

export function formatPrice(amount: string | number, currency: string): string {
  const value = Number(amount);
  if (Number.isNaN(value)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: CURRENCY_FRACTION_DIGITS,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(CURRENCY_FRACTION_DIGITS)}`;
  }
}

export function formatStayPrice(amount: string | number, currency: string, nights: number): string {
  const value = Number(amount) * nights;
  if (Number.isNaN(value)) return '—';
  return formatPrice(value, currency);
}

export async function getLocationTree(api: ApiClient): Promise<readonly CatalogueLocationNode[]> {
  return await api.get<readonly CatalogueLocationNode[]>('/v1/properties/locations/tree');
}

export async function searchStays(
  api: ApiClient,
  params: SearchStaysParams,
): Promise<SearchResult> {
  const { checkIn, checkOut, promo, ...rest } = params;
  return await api.get<SearchResult>('/v1/availability/search', {
    arrival: checkIn,
    departure: checkOut,
    page: rest.page ?? DEFAULT_PAGE,
    pageSize: rest.pageSize ?? DEFAULT_PAGE_SIZE,
    ...(promo !== undefined && promo !== '' ? { promoCode: promo } : {}),
    ...rest,
  });
}

export async function getDetailedResult(
  api: ApiClient,
  params: GetDetailedResultParams,
): Promise<CatalogueDetailedResult> {
  const { promo, ...rest } = params;
  return await api.get<CatalogueDetailedResult>('/v1/availability/detailed', {
    ...rest,
    ...(promo !== undefined && promo !== '' ? { promoCode: promo } : {}),
  });
}
